<script setup lang="ts">
import type { ParsedKevelAd } from '../../utils/parseKevelDecision'

const props = defineProps<{
    ad: ParsedKevelAd
    aspectRatio: string
    aspectRatioClass: string
    aboveTheFold?: boolean
}>()

const { t } = useI18n()
const { trackClick, isValidTarget } = useKevelAdTracking(props.ad, {
    component: 'content-banner-text',
})

// If target URL is invalid, component won't render (handled by parent)
// This is checked here for type safety
if (!isValidTarget && props.ad.targetUrl) {
    console.warn('[kevel] KevelAdContentBanner received invalid target URL:', props.ad.targetUrl)
}

// For video: clicking should play/pause (not navigate)
function handleVideoClick(event: MouseEvent) {
    event.stopPropagation() // Prevent navigation
    const video = event.target as HTMLVideoElement
    if (video.paused) {
        video.play()
    } else {
        video.pause()
    }
}

// Handle container click
function handleContainerClick(event: MouseEvent | KeyboardEvent) {
    if (isValidTarget) {
        trackClick(event instanceof MouseEvent ? event : undefined)
    }
}
</script>

<template>
    <div
        class="kevel-ad-content-banner block relative overflow-hidden"
        :class="{ 'cursor-pointer': isValidTarget }"
        :role="isValidTarget ? 'button' : undefined"
        :tabindex="isValidTarget ? 0 : undefined"
        v-on="isValidTarget ? {
            click: handleContainerClick,
            'keydown.enter': handleContainerClick,
            'keydown.space': (e: KeyboardEvent) => { e.preventDefault(); handleContainerClick(e); }
        } : {}"
    >
        <!-- 2-column grid -->
        <div class="relative w-full h-full grid grid-cols-1 lg:grid-cols-2">
            <!-- Left: Image/Video - always 16:9 -->
            <div class="aspect-video">
                <MiscVideo
                    v-if="ad.isVideo"
                    :src="ad.assetUrl"
                    :alt="ad.headline ?? t('kevel.ad.defaultAlt')"
                    autoplay
                    muted
                    loop
                    :controls="true"
                    class="w-full h-full object-cover cursor-pointer"
                    @click="handleVideoClick"
                />
                <MiscImage
                    v-else
                    :src="ad.assetUrl"
                    :alt="ad.headline ?? t('kevel.ad.defaultAlt')"
                    class="w-full h-full object-cover bg-base-200"
                    :lazy-load="!aboveTheFold"
                    :preload="aboveTheFold ? { fetchPriority: 'high' } : false"
                />
            </div>

            <div
                class="flex flex-col justify-start gap-3 px-6 py-10 lg:py-7 xl:py-10 text-neutral min-h-[300px] lg:aspect-video lg:min-h-0">
                <p class="text-xs uppercase font-normal ml-[13px] tracking-[0.5px]">
                    {{ t('kevel.ad.label') }}
                </p>
                <h2 v-if="ad.headline" class="text-8xl font-normal uppercase font-fjalla-one line-clamp-2">
                    {{ ad.headline }}
                </h2>
                <p v-if="ad.subline" class="text-lg font-normal line-clamp-3">
                    {{ ad.subline }}
                </p>
                <div class="btn btn-md btn-accent w-fit mt-6">
                    <span>{{ t('kevel.ad.moreDetails') }}</span>
                    <MiscIcon name="arrow-right" size="sm" />
                </div>
            </div>
        </div>
    </div>
</template>
